// src/app/services/client.service.ts
import { Injectable, inject } from '@angular/core';
import { 
  Firestore, 
  collection, 
  addDoc, 
  doc, 
  deleteDoc, 
  updateDoc,
  query,
  where,
  getDocs,
  getDoc,
  onSnapshot,
  DocumentData,
  QuerySnapshot
} from '@angular/fire/firestore';
import { Observable } from 'rxjs';
import { Company } from '../models/company.model';

@Injectable({
  providedIn: 'root'
})
export class CompanyService {
  private firestore = inject(Firestore);
private companiesCollection = collection(this.firestore, 'company');

  async getCompaniesSnapshot(): Promise<QuerySnapshot<DocumentData>> {
    return getDocs(this.companiesCollection);
  }

  // Get all companies
  getCompanies(): Observable<Company[]> {
    return new Observable<Company[]>(observer => {
      const unsubscribe = onSnapshot(this.companiesCollection, (snapshot) => {
        const companies = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Company));
        companies.sort((a: any, b: any) => (a.company || '').localeCompare(b.company || ''));
        observer.next(companies);
      }, (error) => observer.error(error));
      return unsubscribe;
    });
  }

  // Add a new client
  addCompany(company: Company) {
    return addDoc(this.companiesCollection, {
      ...company,
      createdAt: new Date()
    });
  }

  // Update an existing client
  updateCompany(id: string, data: Partial<Company>) {
    const docRef = doc(this.firestore, 'company', id);
    return updateDoc(docRef, data);
  }

  // Delete a company
  deleteCompany(id: string) {
    const docRef = doc(this.firestore, 'company', id);
    return deleteDoc(docRef);
  }

  async getCompanyPartners(companyId: string): Promise<any[]> {
    const ownershipRef = collection(this.firestore, 'company_ownerships');
    const qOwnership = query(ownershipRef, where('companyId', '==', companyId));
    const ownershipSnap = await getDocs(qOwnership);
    
    let partnersList: any[] = [];

    if (!ownershipSnap.empty) {
      const ownerships = ownershipSnap.docs.map(d => d.data());
      
      await Promise.all(ownerships.map(async (ownership) => {
        const ownerId = ownership['ownerId'];
        if (ownerId) {
          try {
            const ownerDocRef = doc(this.firestore, 'owners', ownerId);
            const ownerSnap = await getDoc(ownerDocRef);
            
            if (ownerSnap.exists()) {
              const ownerData = ownerSnap.data();
              partnersList.push({
                name: ownerData['name'] || ownerData['email'] || 'Unknown Partner',
                percentage: Number(ownership['profitSharePercent'] || ownership['percentage'] || ownership['ownershipPercentage'] || 0)
              });
            }
          } catch (err) {
            console.error(`Error fetching owner ${ownerId}:`, err);
          }
        }
      }));
    }
    return partnersList;
  }
}
